import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  DialogContentText,
  CircularProgress,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import { timeOffRequestService } from '../../services/timeOffRequestService';
import { formatDateDisplay } from '../../utils/dateUtils';
import { useAuth } from '../../hooks/useAuth';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: '', label: 'All statuses' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' }
];

const statusColor = (status) => {
  switch (status) {
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    case 'cancelled':
      return 'default';
    default:
      return 'warning';
  }
};

const formatRange = (row) => {
  const start = row.start_date ? formatDateDisplay(row.start_date) : '—';
  const end = row.end_date ? formatDateDisplay(row.end_date) : '—';
  if (row.request_mode === 'partial_day') {
    const hrs = row.hours != null ? `${Number(row.hours).toFixed(2)} hrs` : '';
    return `${start}${hrs ? ` · ${hrs}` : ''}`;
  }
  if (start === end) return start;
  return `${start} – ${end}`;
};

const employeeName = (row) =>
  [row.first_name, row.last_name].filter(Boolean).join(' ').trim() || `Employee #${row.employee_id}`;

/** FullCalendar exclusive end: add one day to inclusive end_date. */
function exclusiveEndYmd(ymd) {
  if (!ymd) return ymd;
  const d = new Date(`${String(ymd).slice(0, 10)}T12:00:00`);
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

const TimeOffRequestsPanel = () => {
  const { isManagerOrAdmin } = useAuth();
  const canDelete = typeof isManagerOrAdmin === 'function' ? isManagerOrAdmin() : false;
  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [calendarRows, setCalendarRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [reviewRow, setReviewRow] = useState(null);
  const [reviewStatus, setReviewStatus] = useState('approved');
  const [reviewNotes, setReviewNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await timeOffRequestService.list({
        status: statusFilter || undefined,
        limit: 200
      });
      setRows(res.data ?? []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const loadCalendar = useCallback(async () => {
    try {
      setError('');
      const res = await timeOffRequestService.getCalendar({});
      setCalendarRows(res.data ?? []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load calendar');
    }
  }, []);

  useEffect(() => {
    if (tab === 0) loadList();
    else loadCalendar();
  }, [tab, loadList, loadCalendar]);

  const calendarEvents = useMemo(
    () =>
      (calendarRows || []).map((row) => ({
        id: String(row.id),
        title:
          row.request_mode === 'partial_day'
            ? `${employeeName(row)} (${Number(row.hours).toFixed(2)}h)`
            : employeeName(row),
        start: String(row.start_date).slice(0, 10),
        end: exclusiveEndYmd(row.end_date),
        allDay: true
      })),
    [calendarRows]
  );

  const openReview = (row) => {
    setReviewRow(row);
    setReviewStatus('approved');
    setReviewNotes('');
  };

  const handleReview = async () => {
    if (!reviewRow) return;
    setSaving(true);
    setError('');
    try {
      await timeOffRequestService.review(reviewRow.id, {
        status: reviewStatus,
        review_notes: reviewNotes.trim() || undefined
      });
      setReviewRow(null);
      window.dispatchEvent(new CustomEvent('time-off-requests-updated'));
      await loadList();
      if (tab === 1) await loadCalendar();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save review');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteRow) return;
    setDeleting(true);
    setError('');
    try {
      await timeOffRequestService.delete(deleteRow.id);
      setDeleteRow(null);
      window.dispatchEvent(new CustomEvent('time-off-requests-updated'));
      await loadList();
      if (tab === 1) await loadCalendar();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete request');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Time off requests
        </Typography>
        <Button
          size="small"
          startIcon={<Refresh />}
          onClick={() => (tab === 0 ? loadList() : loadCalendar())}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 1.5 }}>
        <Tab label="Review queue" />
        <Tab label="Calendar" />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {tab === 0 && (
        <>
          <FormControl size="small" sx={{ minWidth: 160, mb: 1.5 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value || 'all'} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Paper>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Submitted</TableCell>
                      <TableCell>When</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No requests found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>
                            {employeeName(row)}
                            {row.employee_number ? ` (${row.employee_number})` : ''}
                          </TableCell>
                          <TableCell>
                            {row.submitted_at ? formatDateDisplay(row.submitted_at) : '—'}
                          </TableCell>
                          <TableCell>{formatRange(row)}</TableCell>
                          <TableCell>
                            {row.request_mode === 'partial_day' ? 'Partial day' : 'Full day(s)'}
                          </TableCell>
                          <TableCell>{row.reason || '—'}</TableCell>
                          <TableCell>
                            <Chip size="small" label={row.status} color={statusColor(row.status)} />
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'inline-flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              {row.status === 'pending' ? (
                                <Button size="small" variant="outlined" onClick={() => openReview(row)}>
                                  Review
                                </Button>
                              ) : null}
                              {canDelete ? (
                                <Button
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  onClick={() => setDeleteRow(row)}
                                >
                                  Delete
                                </Button>
                              ) : null}
                              {row.status !== 'pending' && !canDelete ? '—' : null}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}

      {tab === 1 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Approved time off only. Use this calendar for scheduling reference.
          </Typography>
          <FullCalendar
            plugins={[dayGridPlugin]}
            initialView="dayGridMonth"
            height="auto"
            events={calendarEvents}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: ''
            }}
          />
        </Paper>
      )}

      <Dialog open={!!reviewRow} onClose={() => !saving && setReviewRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Review time-off request</DialogTitle>
        <DialogContent>
          {reviewRow && (
            <Box sx={{ pt: 0.5 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{employeeName(reviewRow)}</strong> · {formatRange(reviewRow)}
              </Typography>
              {reviewRow.reason && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Reason: {reviewRow.reason}
                </Typography>
              )}
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Decision</InputLabel>
                <Select
                  label="Decision"
                  value={reviewStatus}
                  onChange={(e) => setReviewStatus(e.target.value)}
                >
                  <MenuItem value="approved">Approve</MenuItem>
                  <MenuItem value="rejected">Reject</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                size="small"
                label="Note to employee (optional)"
                multiline
                minRows={2}
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewRow(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={reviewStatus === 'rejected' ? 'error' : 'primary'}
            onClick={handleReview}
            disabled={saving}
          >
            {saving ? 'Saving…' : reviewStatus === 'rejected' ? 'Reject' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteRow} onClose={() => !deleting && setDeleteRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete time-off request?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteRow
              ? `Permanently delete ${employeeName(deleteRow)}'s request (${formatRange(deleteRow)})? This cannot be undone.`
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimeOffRequestsPanel;
